import type { Action } from './actions';
import { stepPlayer, type PlayerState } from './physics';
import { CEILING_Y, groundContact } from './player';
import { resolveDamage } from './lives';
import { createRng, type Rng } from './rng';
import { START_LIVES } from '../data/tuning';

/** The six phases of a tick, in the normative order of GDD §5.3. */
export enum TickPhase {
  Input = 0,
  Physics = 1,
  Entities = 2,
  PickupsAndTriggers = 3,
  DamagingCollisions = 4,
  TimersAndScore = 5,
}

export const TICK_PHASES: readonly TickPhase[] = [
  TickPhase.Input,
  TickPhase.Physics,
  TickPhase.Entities,
  TickPhase.PickupsAndTriggers,
  TickPhase.DamagingCollisions,
  TickPhase.TimersAndScore,
];

export const PHASE_NAMES: Readonly<Record<TickPhase, string>> = {
  [TickPhase.Input]: 'input',
  [TickPhase.Physics]: 'physics',
  [TickPhase.Entities]: 'entities',
  [TickPhase.PickupsAndTriggers]: 'pickups-and-triggers',
  [TickPhase.DamagingCollisions]: 'damaging-collisions',
  [TickPhase.TimersAndScore]: 'timers-and-score',
};

/** Something that happened during a tick; dispatched to listeners after phase 6. */
export interface CoreEventInput {
  type: string;
  [detail: string]: unknown;
}

export interface CoreEvent extends CoreEventInput {
  tick: number;
}

export interface TickContext {
  readonly actions: readonly Action[];
  /** Set in phase 1 from the drained actions; read by the physics of phase 2. */
  flap: boolean;
  emit(event: CoreEventInput): void;
}

export type System = (world: CoreWorld, context: TickContext) => void;
export type CoreListener = (event: CoreEvent, world: CoreWorld) => void;

/** Systems by phase, run in phase order and, inside a phase, in registration order. */
export class SystemRegistry {
  private readonly byPhase = new Map<TickPhase, System[]>(TICK_PHASES.map((phase) => [phase, []]));

  register(phase: TickPhase, system: System): void {
    this.byPhase.get(phase)!.push(system);
  }

  run(phase: TickPhase, world: CoreWorld, context: TickContext): void {
    for (const system of this.byPhase.get(phase)!) system(world, context);
  }
}

export interface CoreWorld {
  /** Ticks run so far; during a tick, the number of the tick being run. */
  tick: number;
  player: PlayerState;
  score: number;
  lives: number;
  /** Lives lost in this run, for stats.deaths (GDD §6.4). */
  deaths: number;
  /** Blindaje: absorbs one impact (#79). Lost on respawn (GDD §6.2). */
  armor: boolean;
  /** Soborno: ticks of protection left (#80). Lost on respawn (GDD §6.2). */
  bribeRemaining: number;
  /** Level state (level-state.ts, #50). Only VUELO resolves pickups and damaging collisions. */
  levelState: string;
  /** Dollar bundles towards the next extra life, current.dollars of the save file (GDD §6.4, §7). */
  dollars: number;
  /** Points earned during the current tick, applied in phase 6. */
  pendingScore: number;
  /** Set by the level-end trigger in phase 4; the damaging collisions of that tick are ignored. */
  levelCompleted: boolean;
  /** Countdowns in ticks, checked with remaining > 0 in phase 5 and decremented in phase 6. */
  timers: Record<string, number>;
  readonly rng: Rng;
  readonly config: { ceiling: number; ground: boolean };
  readonly systems: SystemRegistry;
  readonly listeners: CoreListener[];
}

export interface CoreWorldOptions {
  seed: number;
  player?: PlayerState;
  /** Highest centre of the plane; Infinity for a sandbox without ceiling (#28). */
  ceiling?: number;
  /** Whether touching the ground is a damaging collision (#28; El Salto disables it, #93). */
  ground?: boolean;
}

export const GROUND_SOURCE = 'ground';

/** A world with the built-in systems: input, flight physics, ground check and timers and score. */
export function createCoreWorld(options: CoreWorldOptions): CoreWorld {
  const world: CoreWorld = {
    tick: 0,
    player: options.player ?? { x: 0, y: 6, vy: 0 },
    score: 0,
    dollars: 0,
    lives: START_LIVES,
    deaths: 0,
    levelState: 'VUELO',
    armor: false,
    bribeRemaining: 0,
    pendingScore: 0,
    levelCompleted: false,
    timers: {},
    rng: createRng(options.seed),
    config: { ceiling: options.ceiling ?? CEILING_Y, ground: options.ground ?? true },
    systems: new SystemRegistry(),
    listeners: [],
  };
  world.systems.register(TickPhase.Input, (_, context) => {
    context.flap = context.actions.includes('FLAP');
  });
  world.systems.register(TickPhase.Physics, (current, context) => {
    // INTRO and REAPARICION hold the plane still until the first flap (level-state.ts); in the jump zone
    // of El Salto the autopilot of the level runner flies it (#92).
    if (current.levelState === 'INTRO' || current.levelState === 'REAPARICION' || current.levelState === 'SALTO') return;
    current.player = stepPlayer(current.player, { flap: context.flap }, current.config.ceiling);
  });
  world.systems.register(TickPhase.DamagingCollisions, (current, context) => {
    if (current.config.ground && groundContact(current.player)) {
      context.emit({ type: 'DAMAGING_COLLISION', source: GROUND_SOURCE });
    }
  });
  world.systems.register(TickPhase.TimersAndScore, (current, context) => {
    current.score += current.pendingScore;
    current.pendingScore = 0;
    for (const [name, remaining] of Object.entries(current.timers)) {
      if (remaining > 0) current.timers[name] = remaining - 1;
    }
    // The bribe (issue #80) counts down here, after the damage of phase 5 has used it.
    if (current.bribeRemaining > 0) {
      current.bribeRemaining -= 1;
      if (current.bribeRemaining === 0) context.emit({ type: 'powerup:bribe-ended' });
    }
  });
  return world;
}

export function isTimerActive(world: CoreWorld, name: string): boolean {
  return (world.timers[name] ?? 0) > 0;
}

export interface TickResult {
  tick: number;
  events: CoreEvent[];
}

/**
 * Runs one tick (GDD §5.3). The order is fixed here and only here: systems never call each other.
 * Pickups and triggers (phase 4) resolve before damaging collisions (phase 5), so crossing a checkpoint
 * and crashing on the same tick counts the checkpoint. If the level end fires in phase 4, the damaging
 * collisions of that tick are ignored. The damaging collisions of a tick cost one life at most, resolved at
 * the end of phase 5 (#37). Events are dispatched after phase 6, when the tick is final.
 */
export function tick(world: CoreWorld, actions: readonly Action[]): TickResult {
  world.tick += 1;
  const events: CoreEvent[] = [];
  const context: TickContext = {
    actions,
    flap: false,
    emit: (event) => events.push({ ...event, tick: world.tick }),
  };
  for (const phase of TICK_PHASES) {
    // Pickups and triggers happen in flight and in the US sky (its bonus bills, #92); damaging collisions only
    // in flight: nothing hurts after the border (GDD §11.3).
    const inFlight = world.levelState === 'VUELO' || world.levelState === 'CIELO_EEUU';
    if (phase === TickPhase.PickupsAndTriggers && !inFlight) continue;
    if (phase === TickPhase.DamagingCollisions) {
      if (world.levelCompleted || world.levelState !== 'VUELO') continue;
      world.systems.run(phase, world, context);
      resolveDamage(world, context, events);
      continue;
    }
    world.systems.run(phase, world, context);
  }
  for (const event of events) for (const listener of world.listeners) listener(event, world);
  return { tick: world.tick, events };
}

/** Deterministic serialization of the simulated state, with stable key order. */
export function snapshot(world: CoreWorld): string {
  const timers = Object.fromEntries(Object.entries(world.timers).sort(([a], [b]) => a.localeCompare(b)));
  return JSON.stringify({
    tick: world.tick,
    player: { x: world.player.x, y: world.player.y, vy: world.player.vy },
    score: world.score,
    dollars: world.dollars,
    lives: world.lives,
    deaths: world.deaths,
    levelState: world.levelState,
    armor: world.armor,
    bribeRemaining: world.bribeRemaining,
    pendingScore: world.pendingScore,
    levelCompleted: world.levelCompleted,
    timers,
  });
}
